import type { UrlCodec } from '@/utils/urlState';
import { CALENDAR_EVENT_TYPES, CALENDAR_SOURCES, CalendarEventType, CalendarSource } from '@/services/calendarV2Api';

export type CalendarView = 'month' | 'agenda';
export type CalendarAgenda = 'upcoming' | 'past';
export interface CalendarUrlState { view: CalendarView; date: string; agenda: CalendarAgenda; types: CalendarEventType[]; sources: CalendarSource[]; userId?: string; eventId?: string }
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const datePattern = /^\d{4}-\d{2}-\d{2}$/;
export const localDate = (date = new Date()): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
const validDate = (value: string | null, fallback: string): string => {
  if (!value || !datePattern.test(value)) return fallback;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  return localDate(parsed) === value ? value : fallback;
};
const ordered = <T extends string>(values: string[], accepted: readonly T[]): T[] => accepted.filter(value => values.includes(value));
export const parseCalendarUrl = (search: URLSearchParams, today = localDate(), allowedTypes: readonly CalendarEventType[] = CALENDAR_EVENT_TYPES): CalendarUrlState => ({
  view: search.get('view') === 'agenda' ? 'agenda' : 'month',
  date: validDate(search.get('date'), today),
  agenda: search.get('agenda') === 'past' ? 'past' : 'upcoming',
  types: ordered(search.getAll('type'), allowedTypes).length ? ordered(search.getAll('type'), allowedTypes) : [...allowedTypes],
  sources: ordered(search.getAll('source'), CALENDAR_SOURCES).length ? ordered(search.getAll('source'), CALENDAR_SOURCES) : [...CALENDAR_SOURCES],
  userId: uuid.test(search.get('user') || '') ? search.get('user') as string : undefined,
  eventId: uuid.test(search.get('event') || '') ? search.get('event') as string : undefined,
});
export const serializeCalendarUrl = (state: CalendarUrlState, today = localDate(), allowedTypes: readonly CalendarEventType[] = CALENDAR_EVENT_TYPES): URLSearchParams => {
  const result = new URLSearchParams();
  if (state.view !== 'month') result.set('view', state.view);
  if (state.date !== today) result.set('date', validDate(state.date, today));
  if (state.view === 'agenda' && state.agenda !== 'upcoming') result.set('agenda', state.agenda);
  const types = ordered(state.types, allowedTypes);
  if (types.length !== allowedTypes.length) types.forEach(value => result.append('type', value));
  const sources = ordered(state.sources, CALENDAR_SOURCES);
  if (sources.length !== CALENDAR_SOURCES.length) sources.forEach(value => result.append('source', value));
  if (state.userId && uuid.test(state.userId)) result.set('user', state.userId);
  if (state.eventId && uuid.test(state.eventId)) result.set('event', state.eventId);
  return result;
};
export const calendarUrlCodec: UrlCodec<CalendarUrlState> = { parse: params => parseCalendarUrl(params), serialize: value => serializeCalendarUrl(value) };
const dateParts = (value: string): [number, number, number] => value.split('-').map(Number) as [number, number, number];
const utcDate = (year: number, monthIndex: number, day: number): Date => new Date(Date.UTC(year, monthIndex, day));
const dateKey = (value: Date): string => `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, '0')}-${String(value.getUTCDate()).padStart(2, '0')}`;
const addMonthsClamped = (value: string, amount: number): string => {
  const [year, month, day] = dateParts(value); const targetMonth = month - 1 + amount;
  const targetYear = year + Math.floor(targetMonth / 12); const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const lastDay = utcDate(targetYear, normalizedMonth + 1, 0).getUTCDate();
  return dateKey(utcDate(targetYear, normalizedMonth, Math.min(day, lastDay)));
};
const addDays = (value: string, amount: number): string => { const [year, month, day] = dateParts(value); return dateKey(utcDate(year, month - 1, day + amount)); };
export interface CalendarRange { from: string; to: string; visibleFrom: string; visibleTo: string }
export const calendarRange = (state: CalendarUrlState): CalendarRange => {
  const [year, month] = dateParts(state.date); let visibleFrom: string; let visibleTo: string;
  if (state.view === 'month') { visibleFrom = dateKey(utcDate(year, month - 1, 1)); visibleTo = dateKey(utcDate(year, month, 1)); }
  else if (state.agenda === 'past') { visibleFrom = addMonthsClamped(state.date, -6); visibleTo = addDays(state.date, 1); }
  else { visibleFrom = state.date; visibleTo = addMonthsClamped(state.date, 6); }
  const [fromYear, fromMonth, fromDay] = dateParts(visibleFrom); const [toYear, toMonth, toDay] = dateParts(visibleTo);
  const offsetCoverageMs = 14 * 60 * 60 * 1000;
  const from = new Date(Date.UTC(fromYear, fromMonth - 1, fromDay) - offsetCoverageMs).toISOString();
  const to = new Date(Date.UTC(toYear, toMonth - 1, toDay) + offsetCoverageMs).toISOString();
  return { from, to, visibleFrom, visibleTo };
};
